import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import type { MarketProgram } from '@/features/bidding/constants'
import { BidType } from '@/features/bidding/constants'
import { findLatestAcceptedBid, GET_LATEST_BID_API_ID } from '@/features/bidding/endpoints/bids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { EnergyBidSnapshot } from '@/features/bidding/types/bidDetail/EnergyBidSnapshot'
import { toEnergyBidSnapshot } from '@/features/bidding/utils/bidDetail/energy/toEnergyBidSnapshot'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

const QUERY_KEY = 'LATEST_ENERGY_BID'

export type UseLatestEnergyBidQueryParams = Readonly<{
  portfolioCode: string | null
  deliveryDay: MarketDate | null
  marketProgram: MarketProgram
  activationGroupUuids: ReadonlyArray<string>
}>

export type UseLatestEnergyBidQueryResult = Readonly<{
  snapshots: ReadonlyArray<EnergyBidSnapshot>
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  invalidate: () => void
}>

export function useLatestEnergyBidQuery(params: UseLatestEnergyBidQueryParams): UseLatestEnergyBidQueryResult {
  const { portfolioCode, deliveryDay, marketProgram, activationGroupUuids } = params
  const queryClient = useQueryClient()

  const { snapshots, isLoading, isFetching, isError } = useQueries({
    queries: activationGroupUuids.map((activationGroupUuid) => ({
      queryKey: [
        QUERY_KEY,
        GET_LATEST_BID_API_ID,
        portfolioCode,
        deliveryDay?.toISODate(),
        marketProgram,
        activationGroupUuid,
      ],
      queryFn: async (): Promise<EnergyBidSnapshot[]> => {
        if (!deliveryDay) {
          return []
        }
        const bid = await findLatestAcceptedBid(deliveryDay, activationGroupUuid, BidType.ENERGY)

        return bid ? [toEnergyBidSnapshot(bid)] : []
      },
      enabled: portfolioCode !== null && deliveryDay !== null,
    })),
    combine: (results) => ({
      snapshots: results.flatMap((r) => r.data ?? []) as ReadonlyArray<EnergyBidSnapshot>,
      isLoading: results.some((r) => r.isLoading),
      isFetching: results.some((r) => r.isFetching),
      isError: results.some((r) => r.isError),
    }),
  })

  useErrorHandler(isError)

  const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }), [queryClient])

  return { snapshots, isLoading, isFetching, isError, invalidate }
}
